import React, { useState } from 'react';
import { TitleContainer, WrapperInput, WrapperButtons, ErrorMessageContainer, ErrorMessageText, ErrorMessageButtonWrapper, FormContainer } from './EmailVerification.styles';
import { TextInput } from '@/components/input/TextInput/TextInput';
import { useAppSelector } from '@/app/hooks';
import { handleGetStarted } from '@/utils/handleEndpoints';
import { useDispatch } from 'react-redux';
import { PropertyType } from '@/types';
import { Button } from '@/components/Button/DefaultButton/Button';
import * as yup from 'yup';
import _ from 'lodash';
import { TextBlock, TypeOfText } from '../../text/TextBlock';
import { Spinner } from '../../RefreshableCurrencyInput/RefreshableCurrencyInput.styles';

interface EmailFormValues {
  email: string;
}

interface ModalEmailVerificationProps {
  sendCodeToEmail: () => Promise<void>;
  setFormValues: React.Dispatch<React.SetStateAction<EmailFormValues>>;
  formValues: EmailFormValues;
  handleStartNewApplication: () => void;
  resumeOnly: boolean;
}

export const EmailVerification = ({ sendCodeToEmail, setFormValues, formValues }: ModalEmailVerificationProps) => {
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string>('Begin Your Application');
  const [errorButtonTitle, setErrorButtonTitle] = useState<string>('Continue');
  const [isResumeError, setIsResumeError] = useState<boolean>(false);

  const formSchema = yup.object({
    email: yup
      .string()
      .email()
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
  });

  const handleChange = _.debounce((value: string | number | boolean, name: string) => {
    setFormValues((prevValues: EmailFormValues) => ({
      ...prevValues,
      [name]: value,
    }));
    let obj: Record<string, string | number | boolean> = {};
    obj[name] = value;
  }, 100);

  const handleClick = async () => {
    setIsLoading(true);
    let auxLoanApplication = { ...loanApplication };

    const resumeEligibilityWindow = {
      fromHours: 72,
      toHours: 24,
    };

    try {
      const response = await handleGetStarted(true, auxLoanApplication, dispatch, formValues.email);

      if (response && response.data.isEligible) {
        const createdAt = response.data.createdAt;

        if (createdAt) {
          const created = new Date(createdAt);
          const now = new Date();
          const diffInHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);

          const isWithinWindow = diffInHours >= resumeEligibilityWindow.toHours && diffInHours <= resumeEligibilityWindow.fromHours;

          if (!isWithinWindow) {
            setErrorMessage('Your application cannot be resumed at this time. Please contact support or start a new application.');
            return;
          }
        }
        return sendCodeToEmail();
      } else if (response && response.data.isEligible === false) {
        setErrorMessage("We couldn't find an open application with that email. Click continue to start a new application.");
      }
    } catch (error: unknown) {
      const enhancedError = error as any;

      setIsResumeError(true);
      setErrorTitle(enhancedError.errorResponse?.response?.error?.appActionTitle || 'Error');
      setErrorButtonTitle(enhancedError.errorResponse?.response?.error?.appActionButtonTitle || 'Continue');

      if (typeof error === 'string') {
        setErrorMessage(error);
      } else if (enhancedError.message) {
        setErrorMessage(enhancedError.message);
      } else {
        setErrorMessage('An error occurred while processing your request. Please try again.');
      }
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleErrorClose = () => {
    if (isResumeError) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = templateConfig?.pages?.returnPage || '';
    } else {
      setErrorMessage(null);
      dispatch({
        type: 'CurrentForm/addField',
        payload: { email: formValues.email },
      });

      dispatch({ type: 'CurrentPage/setIsResume', payload: false });
      dispatch({ type: 'CurrentPage/setIsNewApplication', payload: true });
      setIsResumeError(false);
    }
  };

  const handleIsDisabled = (): boolean => {
    try {
      const email = formValues?.email || '';
      return !formSchema.isValidSync({ email });
    } catch (error) {
      console.error(error);
      return true;
    }
  };

  return (
    <>
      <TitleContainer data-testid="title-container" className="">
        <TextBlock text={errorTitle} direction={'center'} color="primary" type={TypeOfText.H2} />
        {!errorMessage && <TextBlock text={'Enter your email to resume or begin a new application'} direction={'center'} color="default" type={TypeOfText.Subtitle} />}
        {errorMessage && (
          <ErrorMessageContainer>
            <ErrorMessageText>{errorMessage}</ErrorMessageText>
            <ErrorMessageButtonWrapper>
              <Button label={errorButtonTitle} onClick={handleErrorClose} variant="primary" buttonSize="s" />
            </ErrorMessageButtonWrapper>
          </ErrorMessageContainer>
        )}
      </TitleContainer>
      {!errorMessage && (
        <FormContainer
          onSubmit={(e) => {
            e.preventDefault();
            if (!handleIsDisabled() && !isLoading) {
              handleClick();
            }
          }}
        >
          <WrapperInput $isHidden={false}>
            <TextInput propertyType={PropertyType.Email} isCentered placeholder="Email Address" dataTestId="email" size={'50%'} type="email" onChange={handleChange} />
          </WrapperInput>
          <WrapperButtons>
            <Button label={isLoading ? <Spinner /> : 'Continue'} onClick={handleClick} variant="primary" buttonSize="s" isDisabled={handleIsDisabled() || isLoading} />
          </WrapperButtons>
        </FormContainer>
      )}
    </>
  );
};
